import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectsApi } from '../../../api/projects';
import { ApiError } from '../../../api/client';
import { useToast } from '../../../components/Toast';
import { usePermission } from '../../../auth/AuthContext';
import { Project, ProjectBudgetLine } from '../../../types/projects';
import { BUDGET_CATEGORIES, BudgetCategoryKey } from './budgetCategories';
import { CommentThread } from '../../../components/CommentThread';

const lineSchema = z.object({
  description: z.string().min(2, 'Description is required.'),
  remarks: z.string().optional(),
  unitDescription: z.string().optional(),
  unitQuantity: z.coerce.number().positive('Must be greater than zero.'),
  unitCost: z.coerce.number().positive('Must be greater than zero.'),
});
type LineFormValues = z.infer<typeof lineSchema>;

function money(n: number) {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Section 21-25: Subtotal = Quantity x Unit Cost, computed by the
 * server on every add. Category subtotals and the grand total shown
 * here are pure client-side sums over what the server already
 * computed - never entered by the user, and they recompute
 * automatically whenever a line is added (React Query invalidation
 * refetches the full list, so this is always in sync).
 */
export function ProjectBudgetTab() {
  const { project } = useOutletContext<{ project: Project }>();
  const canManage = usePermission('project_budget.manage');
  const queryClient = useQueryClient();

  const budgetQuery = useQuery({ queryKey: ['project-budget', project.id], queryFn: () => projectsApi.listBudgetLines(project.id) });
  const lines = budgetQuery.data?.data ?? [];

  const grandTotal = lines.reduce((sum, l) => sum + Number(l.line_total ?? l.planned_amount), 0);

  return (
    <>
      <section className="card budget-summary-card">
        <h2>Budget</h2>
        <div className="budget-summary-row">
          <div><span className="muted">Approved budget</span><strong>{project.currency_code} {money(Number(project.budget_total))}</strong></div>
        </div>
        <CommentThread projectId={project.id} entityType="budget" />
      </section>

      {BUDGET_CATEGORIES.map((cat) => (
        <BudgetCategorySection
          key={cat.key}
          projectId={project.id}
          currencyCode={project.currency_code}
          category={cat}
          lines={lines.filter((l) => l.category === cat.key)}
          canManage={canManage}
          onChanged={() => queryClient.invalidateQueries({ queryKey: ['project-budget', project.id] })}
        />
      ))}

      <section className="card grand-total-card">
        <h2>Grand Total</h2>
        <p className="grand-total-amount">{project.currency_code} {money(grandTotal)}</p>
      </section>
    </>
  );
}

function BudgetCategorySection({
  projectId, currencyCode, category, lines, canManage, onChanged,
}: {
  projectId: number;
  currencyCode: string;
  category: { key: BudgetCategoryKey; code: string; label: string };
  lines: ProjectBudgetLine[];
  canManage: boolean;
  onChanged: () => void;
}) {
  const { showSuccess, showError } = useToast();
  const [formOpen, setFormOpen] = useState(false);
  const { register, handleSubmit, reset, formState: { errors } } = useForm<LineFormValues>({
    resolver: zodResolver(lineSchema),
    defaultValues: { unitQuantity: 1 },
  });

  const createMutation = useMutation({
    mutationFn: (values: LineFormValues) => projectsApi.addBudgetLine(projectId, { ...values, category: category.key, currencyCode }),
    onSuccess: () => { showSuccess('Budget line added.'); onChanged(); reset({ unitQuantity: 1 }); setFormOpen(false); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to add budget line.'),
  });

  const categorySubtotal = lines.reduce((sum, l) => sum + Number(l.line_total ?? l.planned_amount), 0);

  return (
    <section className="card budget-category-card">
      <div className="page-header">
        <h3><span className="code-badge">{category.code}</span> {category.label}</h3>
        {canManage && <button className="btn-accent-3" onClick={() => setFormOpen((v) => !v)}>{formOpen ? 'Cancel' : '+ Add Line'}</button>}
      </div>

      {lines.length === 0 && <p className="muted">No budget lines in this category yet.</p>}

      {lines.length > 0 && (
        <div className="gantt-scroll">
          <table className="budget-table">
            <thead>
              <tr>
                <th>Code</th><th>Budget Line</th><th>Unit</th><th>Quantity</th><th>Unit Cost</th><th>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const subtotal = Number(l.line_total ?? l.planned_amount);
                return (
                  <tr key={l.id}>
                    <td><span className="code-badge code-badge-activity">{l.line_code}</span></td>
                    <td>{l.description}{l.remarks && <div className="muted budget-remarks">{l.remarks}</div>}</td>
                    <td>{l.unit_description ?? '-'}</td>
                    <td>{Number(l.unit_quantity)}</td>
                    <td>{money(Number(l.unit_cost ?? 0))}</td>
                    <td><strong>{money(subtotal)}</strong></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="muted budget-category-total">{category.code} Subtotal: <strong>{currencyCode} {money(categorySubtotal)}</strong></p>

      {formOpen && (
        <form className="inline-form budget-line-form" onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
          <label>Budget Line Description<input {...register('description')} />{errors.description && <span className="field-error">{errors.description.message}</span>}</label>
          <label>Remarks (optional)<textarea rows={2} {...register('remarks')} /></label>
          <label>Unit<input placeholder="e.g. month, school, lump sum" {...register('unitDescription')} /></label>
          <label>Quantity<input type="number" step="0.01" {...register('unitQuantity')} />{errors.unitQuantity && <span className="field-error">{errors.unitQuantity.message}</span>}</label>
          <label>Unit Cost<input type="number" step="0.01" {...register('unitCost')} />{errors.unitCost && <span className="field-error">{errors.unitCost.message}</span>}</label>
          <button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? 'Saving…' : 'Add'}</button>
        </form>
      )}
    </section>
  );
}
